import { BOARD } from "@grundbesitz/shared";
import type { PublicGameState, RoomMember } from "@grundbesitz/shared";
import { assetGroupOf, auctionSubject } from "../../../lib/assets";
import { formatMoney } from "../../../lib/format";
import { PropertyCard } from "../../board/PropertyCard";
import { PlayerChip } from "../assets/PlayerChip";

const REASON = {
  DECLINED: "Der Kauf wurde abgelehnt, jetzt bieten alle.",
  BANKRUPTCY: "Nach einem Bankrott gegenüber der Bank wird das Grundstück versteigert.",
  BUILDING_SHORTAGE: "Die Bank hat nicht genug Gebäude für alle, die bauen wollen.",
} as const;

/** What is being auctioned: field facts or the scarce building, plus who wants to build where. */
export function Subject({
  game,
  members,
  pid,
}: {
  game: PublicGameState;
  members: RoomMember[];
  pid: string | null;
}) {
  const a = game.auction;
  if (!a) return null;
  const subject = auctionSubject(game, a, pid);
  const field = subject.field !== null ? BOARD[subject.field] : null;
  const group = subject.field !== null ? assetGroupOf(subject.field) : null;
  const supply = a.buildingKind === "hotel" ? game.bank.hotels : game.bank.houses;

  return (
    <section aria-label="Gegenstand der Versteigerung" className="relative overflow-hidden rounded-xl border border-line bg-surface-2 py-3 pl-5 pr-3">
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5" style={{ background: group?.color ?? "var(--ink-2)" }} />
      <p className="text-xs text-ink-2">{group ? group.name : "Gebäude"}</p>
      <h2 className="font-display text-2xl font-semibold leading-tight">{subject.title}</h2>

      {field && (field.kind === "STREET" || field.kind === "RAILROAD" || field.kind === "UTILITY") && (
        <>
          <dl className="mt-1.5 flex flex-wrap gap-x-5 gap-y-0.5 text-sm">
            <div className="flex gap-1.5">
              <dt className="text-ink-2">Kaufpreis</dt>
              <dd className="font-semibold tabular-nums">{formatMoney(field.price)}</dd>
            </div>
            <div className="flex gap-1.5">
              <dt className="text-ink-2">Hypothek</dt>
              <dd className="font-semibold tabular-nums">{formatMoney(field.mortgage)}</dd>
            </div>
            {field.kind !== "UTILITY" && (
              <div className="flex gap-1.5">
                <dt className="text-ink-2">Grundmiete</dt>
                <dd className="font-semibold tabular-nums">{formatMoney(field.rents[0])}</dd>
              </div>
            )}
          </dl>
          <details className="mt-2 text-sm">
            <summary className="min-h-8 cursor-pointer py-1 font-semibold text-accent">Besitzrechtkarte zeigen</summary>
            <div className="mt-1">
              <PropertyCard field={field.index} game={game} members={members} />
            </div>
          </details>
        </>
      )}

      {a.kind === "BUILDING" && (
        <div className="mt-1.5 space-y-1 text-sm">
          <p>
            Die Bank hat noch{" "}
            <strong className="tabular-nums">
              {supply} {a.buildingKind === "hotel" ? (supply === 1 ? "Hotel" : "Hotels") : supply === 1 ? "Haus" : "Häuser"}
            </strong>
            . Das Höchstgebot ersetzt den Listenpreis.
          </p>
          {subject.requests.length > 0 && (
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {subject.requests.map((r) => (
                <li key={r.playerId} className="flex flex-wrap items-center gap-1.5">
                  <PlayerChip game={game} members={members} id={r.playerId} size={16} you={r.playerId === pid} />
                  <span className="text-ink-2">will auf {BOARD[r.field].name} bauen</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <p className="mt-1.5 text-xs text-ink-2">{REASON[a.reason]}</p>
    </section>
  );
}
